import { formatDateKey, parseDateKey } from "@/lib/time";

export function addDaysToDateKey(dateKey: string, days: number) {
  const date = parseDateKey(dateKey);
  date.setDate(date.getDate() + days);
  return formatDateKey(date);
}

export function dateKeyFromValue(value: Date | string | null | undefined) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : formatDateKey(date);
}

export function toggleDateSplit({
  startDate,
  endDate,
  startTime = "",
  endTime = "",
}: {
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
}) {
  const isSplit = Boolean(startDate && endDate && startDate !== endDate);
  if (!startDate) return { endDate, endTime };
  if (!isSplit) return { endDate: addDaysToDateKey(startDate, 1), endTime };
  return {
    endDate: startDate,
    endTime: startTime && endTime && endTime <= startTime ? "" : endTime,
  };
}

export function rangeDurationMinutes(
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
) {
  if (!startDate || !startTime || !endDate || !endTime) return null;
  const start = combineDateTime(startDate, startTime);
  const end = combineDateTime(endDate, endTime);
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60_000));
}

export function endOfRange(startDate: string, startTime: string, durationMin: number) {
  if (!startDate || !startTime || !Number.isFinite(durationMin) || durationMin <= 0) return null;
  const end = new Date(combineDateTime(startDate, startTime).getTime() + durationMin * 60_000);
  return {
    date: formatDateKey(end),
    time: `${String(end.getHours()).padStart(2, "0")}:${String(end.getMinutes()).padStart(2, "0")}`,
  };
}

export function shiftTimeRangeDate({
  target,
  startDate,
  endDate,
  startTime,
  endTime,
  offset,
}: {
  target: "start" | "end";
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  offset: -1 | 1;
}) {
  const selectedDate = target === "start" ? startDate : endDate;
  if (!selectedDate) return { startDate, endDate };
  const candidate = addDaysToDateKey(selectedDate, offset);

  if (startTime && endTime) {
    const nextStart = combineDateTime(target === "start" ? candidate : startDate, startTime);
    const nextEnd = combineDateTime(target === "end" ? candidate : endDate, endTime);
    if (nextEnd.getTime() < nextStart.getTime()) return { startDate, endDate };
  }

  return target === "start"
    ? { startDate: candidate, endDate }
    : { startDate, endDate: candidate };
}

function combineDateTime(dateKey: string, time: string) {
  const [year, month, day] = dateKey.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
}
